import { setValue } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";
import { JQueryInput } from "./types";

const skyward = window.skyward;
skyward.checkboxGroup = skyward.checkboxGroup || {};

const classNames =
{
    uiDisabled: "ui-disabled",
    checkboxGroupRow: "js-checkboxGroup__row",
    checkboxGroupSelectAll: "js-checkboxGroup__selectAll"
} as const;

const pendUpdateSelectAll = makeBatched(updateSelectAll);
const pendUpdateVisibility = makeBatched(updateGroupVisibility);

const events =
{
    init
};

registerEvents("checkboxGroup", events);

skyward.checkboxGroup.toggleAll = toggleAll;
/**
 * @description Set or clear all checkboxes within a checkbox group.
 * @param group The .checkboxGroup element making up the root of the checkbox group.
 * @param inputValue Whether to set or clear the checkboxes.
 */
export function toggleAll(group: JQuery<HTMLElement>, inputValue?: boolean)
{
    const selectAllButton = group.find(`.${classNames.checkboxGroupSelectAll}`);
    const allCheckboxes = group
        .find(`.${classNames.checkboxGroupRow}:not(:inherentlyHidden) input[type="checkbox"]:not(:inherentlyHidden)`)
        .not(selectAllButton) as JQuery<HTMLInputElement>;

    const enabledCheckboxes = allCheckboxes.filter(function ()
    {
        return !$(this).hasClass(classNames.uiDisabled) && this.style.display !== "none";
    });

    // Indeterminate and checked => unchecked, unchecked => checked.
    const value = inputValue !== undefined ? inputValue : getSelectAllState(enabledCheckboxes) === false ? true : false;

    enabledCheckboxes.each(function ()
    {
        // Checkboxes' checked states can change in the loop due to dependencies from previous checkboxes, so we have to check again if we
        // should still update them.
        if (!this.indeterminate && this.checked === value)
        {
            return;
        }
        if ($(this).hasClass(classNames.uiDisabled) || $(this).closest(`.${classNames.checkboxGroupRow}:inherentlyHidden`).length !== 0)
        {
            return;
        }
        setValue(this, value);
    });
}

function init(checkboxGroup: JQuery<HTMLElement>)
{
    if (!checkboxGroup.attr("aria-label") && !checkboxGroup.attr("aria-labelledby"))
    {
        let label = checkboxGroup.findLabel();

        if (label.length > 0)
        {
            const id = label.addIds().attr("id")!;
            checkboxGroup.attr("aria-labelledby", id);
        }
    }

    checkboxGroup.on("change readonly", `input:not(.${classNames.checkboxGroupSelectAll})`, function ()
    {
        pendUpdateSelectAll(checkboxGroup);
    });

    const toggleAllDebounced = skyward.debounce(toggleAll, 10);

    // Only bind the selectAll change event if there isn't an inline onchange override
    checkboxGroup.find(`.${classNames.checkboxGroupSelectAll}`).filter((_, element) => !$(element).attr("onchange")).on("change", function (_, isFromSetSelectAll)
    {
        const input = this as HTMLInputElement;
        if (!$(input).hasClass(classNames.uiDisabled) && !isFromSetSelectAll)
        {
            toggleAllDebounced(checkboxGroup);
        }
    });

    checkboxGroup.on("visible", `.${classNames.checkboxGroupRow}`, function (event)
    {
        $(event.target).animationPromise().done(function ()
        {
            pendUpdateSelectAll(checkboxGroup);
        });
        pendUpdateVisibility(checkboxGroup);
    });

    updateSelectAll(checkboxGroup);
}

function setSelectAll(selectAllButton: JQuery<HTMLInputElement>, state?: boolean)
{
    const originalChecked = selectAllButton[0].checked;

    if (state === undefined)
    {
        selectAllButton[0].indeterminate = true;
        selectAllButton[0].checked = true;
    }
    else
    {
        selectAllButton[0].indeterminate = false;
        selectAllButton[0].checked = !!state;
    }

    if (originalChecked !== selectAllButton[0].checked)
    {
        const isFromSetSelectAll = true;
        selectAllButton.trigger("change", [isFromSetSelectAll]);
    }
}

function updateSelectAll(group: JQuery<HTMLElement>)
{
    const selectAllButton = group.find<HTMLInputElement>(`.${classNames.checkboxGroupSelectAll}`);
    const checkboxes = group.find(`.${classNames.checkboxGroupRow}:not(:inherentlyHidden) input[type="checkbox"]:not(:inherentlyHidden)`).not(selectAllButton) as JQuery<HTMLInputElement>;
    const state = getSelectAllState(checkboxes);
    setSelectAll(selectAllButton, state);
    const enabledCheckboxes = checkboxes.filter(function () { return !$(this).hasClass(classNames.uiDisabled); });
    selectAllButton.first().toggleClass(classNames.uiDisabled, enabledCheckboxes.length === 0);
}

function updateGroupVisibility(group: JQuery<HTMLElement>)
{
    const rows = group.find(`.${classNames.checkboxGroupRow}:not(.checkboxGroup__selectAll)`);
    const fieldWrapper = group.closest(".fieldWrapper");
    const fieldWrapperIsVisible = willBeVisible(fieldWrapper);
    const visibleRows = rows.toArray().filter(function (row: JQueryInput) { return willBeVisible($(row)); });
    const anyRowsVisible = visibleRows.length !== 0;
    if (fieldWrapperIsVisible !== anyRowsVisible)
    {
        const animate = visibleRows.some(function (row: JQueryInput) { return $(row).isAnimating(); });
        if (animate)
        {
            fieldWrapper.animateToggle(anyRowsVisible);
        }
        else
        {
            fieldWrapper.toggle(anyRowsVisible);
        }
    }

    function willBeVisible(element: JQuery<HTMLElement>)
    {
        return element.isAnimatingOpen() || !element.is(":inherentlyHidden") && !element.isAnimatingClosed();
    }
}

function getSelectAllState(checkboxes: JQuery<HTMLInputElement>)
{
    if (checkboxes.length === 0)
    {
        return false;
    }
    if (checkboxes[0].indeterminate)
    {
        return undefined;
    }

    const initialState = checkboxes[0].checked;
    for (let i = 1; i < checkboxes.length; i++)
    {
        if (checkboxes[i].indeterminate || checkboxes[i].checked !== initialState)
        {
            return undefined;
        }
    }
    return initialState;
}

function makeBatched(callback: (group: JQuery<HTMLElement>) => void)
{
    const pendingCallbacks = new Set<JQuery<HTMLElement>>();
    const queueCallback = window.queueMicrotask || setTimeout;
    return function (arg: JQuery<HTMLElement>)
    {
        if (pendingCallbacks.size === 0)
        {
            queueCallback(function ()
            {
                pendingCallbacks.forEach(callback);
                pendingCallbacks.clear();
            });
        }
        pendingCallbacks.add(arg);
    };
}